import { useState, useEffect } from "react";
import modulosService from "../../services/modulosService";
import { Plus, X } from "lucide-react";
import {
  FK, type as T, Field, Row, Section, TextInput, Select, EstadoRow, Pill, JsonField, FkOutline, FkToggle,
} from "../product_config/formkit";

export default function ConfiguracionJsonTab({
  jsonConfig,
  handleJsonConfigChange,
  formData
}) {
  const [nuevoCampo, setNuevoCampo] = useState({ nombre: "", label: "", tipo: "personal" });
  const [nuevaOpcion, setNuevaOpcion] = useState({});

  // catalogo: todos los campos definidos para este plan (activos o no)
  const catalogo = Array.isArray(jsonConfig?.catalogo_campos) ? jsonConfig.catalogo_campos : [];
  // seleccionados: los que están activos (se incluyen en el JSON final)
  const seleccionados = Array.isArray(jsonConfig?.campos_opcionales) ? jsonConfig.campos_opcionales : [];

  // Catálogo de módulos disponibles, leído desde flow (MODULES_CONFIG vía /api/modules).
  const [modulosCatalogo, setModulosCatalogo] = useState([]);
  useEffect(() => {
    let activo = true;
    modulosService.getModulosDisponibles().then((modulos) => {
      if (activo) setModulosCatalogo(modulos);
    });
    return () => { activo = false; };
  }, []);

  // Convierte un id sin label conocido en algo legible: "titulo_profesional" -> "Titulo Profesional"
  const humanizarId = (id) =>
    String(id)
      .split('_')
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
      .join(' ');

  // Lista final a renderizar: catálogo de flow + cualquier módulo que venga en el JSON
  // y no esté en el catálogo (marcado como "adicional"), para que nunca quede invisible.
  const modulosSeleccionadosIds = Array.isArray(jsonConfig?.modulos_requeridos) ? jsonConfig.modulos_requeridos : [];
  const idsEnCatalogo = new Set(modulosCatalogo.map((m) => m.id));
  const modulosAdicionales = modulosSeleccionadosIds
    .filter((id) => !idsEnCatalogo.has(id))
    .map((id) => ({ id, label: humanizarId(id), adicional: true }));
  const modulosARenderizar = [...modulosCatalogo, ...modulosAdicionales];

  const isActivo = (campoId) => seleccionados.some(c => c.campo === campoId);
  const getCampoActivo = (campoId) => seleccionados.find(c => c.campo === campoId);
  const getCampoCatalogo = (campoId) => catalogo.find(c => c.campo === campoId);

  const toggleCampo = (campoCatalogo, checked) => {
    if (checked) {
      const entry = { campo: campoCatalogo.campo, opciones: campoCatalogo.opciones ?? [] };
      if (campoCatalogo.tipo) entry.tipo = campoCatalogo.tipo;
      handleJsonConfigChange("campos_opcionales", [...seleccionados, entry]);
    } else {
      handleJsonConfigChange("campos_opcionales", seleccionados.filter(c => c.campo !== campoCatalogo.campo));
    }
  };

  const updateOpciones = (campoId, opciones) => {
    // Actualiza en seleccionados
    handleJsonConfigChange("campos_opcionales", seleccionados.map(c =>
      c.campo === campoId ? { ...c, opciones } : c
    ));
    // Actualiza también en catálogo
    handleJsonConfigChange("catalogo_campos", catalogo.map(c =>
      c.campo === campoId ? { ...c, opciones } : c
    ));
  };

  const agregarOpcion = (campoId) => {
    const val = (nuevaOpcion[campoId] || "").trim();
    if (!val) return;
    const campo = getCampoActivo(campoId) || getCampoCatalogo(campoId);
    if (campo && !campo.opciones.includes(val)) {
      updateOpciones(campoId, [...campo.opciones, val]);
    }
    setNuevaOpcion(prev => ({ ...prev, [campoId]: "" }));
  };

  const eliminarOpcion = (campoId, opcion) => {
    const campo = getCampoActivo(campoId) || getCampoCatalogo(campoId);
    if (campo) updateOpciones(campoId, campo.opciones.filter(o => o !== opcion));
  };

  const agregarCampo = () => {
    const campoId = nuevoCampo.nombre.trim().toLowerCase().replace(/\s+/g, "_");
    const label = nuevoCampo.label.trim() || campoId;
    if (!campoId || catalogo.some(c => c.campo === campoId)) return;
    handleJsonConfigChange("catalogo_campos", [...catalogo, { campo: campoId, label, tipo: nuevoCampo.tipo, opciones: [] }]);
    setNuevoCampo({ nombre: "", label: "", tipo: "personal" });
  };

  const TIPOS_CAMPO = [
    { value: "salud",      label: "Salud" },
    { value: "incendio",   label: "Incendio" },
    { value: "hogar",      label: "Hogar" },
    { value: "auto",       label: "Auto" },
    { value: "otro",       label: "Otro" },
  ];

  const eliminarCampo = (campoId) => {
    handleJsonConfigChange("catalogo_campos", catalogo.filter(c => c.campo !== campoId));
    handleJsonConfigChange("campos_opcionales", seleccionados.filter(c => c.campo !== campoId));
  };

  // Las 3 features de la card. Se pasa el arreglo completo —como campos_opcionales—
  // en vez de un dot-path con índice: el walker de handleJsonConfigChange hace una
  // copia superficial, así que `featuresCard.0` mutaría el arreglo del estado anterior.
  const features = Array.isArray(jsonConfig?.featuresCard) ? jsonConfig.featuresCard : [];

  const handleFeatureChange = (i, valor) => {
    const siguiente = [...features];
    siguiente[i] = valor;
    // Se recorta la cola vacía para no persistir tres strings vacíos.
    while (siguiente.length > 0 && !String(siguiente[siguiente.length - 1]).trim()) {
      siguiente.pop();
    }
    handleJsonConfigChange("featuresCard", siguiente);
  };

  const EDADES = [
    { key: "edad_titular", label: "Edad titular" },
    { key: "edad_conyuge", label: "Edad cónyuge" },
    { key: "edad_hijo", label: "Edad hijo" },
  ];

  const iconBtn = { appearance: "none", border: "none", background: "transparent", cursor: "pointer", color: FK.muted, padding: 4, borderRadius: 6, display: "grid", placeItems: "center" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {/* Identificación */}
      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        <Row>
          <Field label="Tipo" htmlFor="jc-tipo">
            <TextInput id="jc-tipo" value={jsonConfig.tipo} onChange={(e) => handleJsonConfigChange('tipo', e.target.value)} placeholder="ej: dental, salud, vida" />
          </Field>
          <Field label="Capital" htmlFor="jc-capital">
            <TextInput id="jc-capital" type="number" value={jsonConfig.capital} onChange={(e) => handleJsonConfigChange('capital', parseInt(e.target.value) || '')} placeholder="ej: 100" />
          </Field>
        </Row>
        <Field label="Código plan" htmlFor="jc-codigo" full>
          <TextInput id="jc-codigo" value={jsonConfig.codigo_plan} onChange={(e) => handleJsonConfigChange('codigo_plan', e.target.value)} placeholder="ej: DENT100" />
        </Field>
      </div>

      {/* Badge */}
      <Section title="Configuración de badge" desc="Etiqueta destacada que aparece sobre la card del plan.">
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <Row>
            <Field label="Tipo badge" htmlFor="jc-btipo">
              <TextInput id="jc-btipo" value={jsonConfig.badge.tipo} onChange={(e) => handleJsonConfigChange('badge.tipo', e.target.value)} placeholder="ej: basico, premium" />
            </Field>
            <Field label="Texto badge" htmlFor="jc-btexto">
              <TextInput id="jc-btexto" value={jsonConfig.badge.texto} onChange={(e) => handleJsonConfigChange('badge.texto', e.target.value)} placeholder="ej: Básico, Premium" />
            </Field>
          </Row>
          <EstadoRow titulo="Badge visible" texto="Muestra el badge en la card del plan." on={jsonConfig.badge.visible} onChange={(v) => handleJsonConfigChange('badge.visible', v)} />
        </div>
      </Section>

      {/* Lo que incluye · alimenta el bloque "Incluye" de la card del miniform en flow */}
      <Section
        title="Lo que incluye (card)"
        desc="Hasta 3 frases cortas que se muestran en la card de resultado del miniform. Sin punto final. Si se dejan vacías, la card no muestra el bloque."
      >
        {[0, 1, 2].map((i) => (
          <Row key={i}>
            <Field label={`Feature ${i + 1}`} htmlFor={`jc-feat-${i}`}>
              <TextInput
                id={`jc-feat-${i}`}
                value={features[i] || ""}
                onChange={(e) => handleFeatureChange(i, e.target.value)}
                placeholder={
                  [
                    "ej: Consultas médicas y exámenes",
                    "ej: Hospitalización y cirugías",
                    "ej: Reembolso 100% digital",
                  ][i]
                }
              />
            </Field>
          </Row>
        ))}
      </Section>

      {/* Restricciones de edad */}
      <Section title="Restricciones de edad" desc="Rango etario permitido por integrante.">
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {EDADES.map(({ key, label }) => (
            <div key={key}>
              <div style={{ fontSize: 13, fontWeight: 600, color: FK.text, marginBottom: 8 }}>{label}</div>
              <Row>
                <Field label="Mínima" htmlFor={`${key}-min`}>
                  <TextInput id={`${key}-min`} type="number" value={jsonConfig.restricciones[key].min} onChange={(e) => handleJsonConfigChange(`restricciones.${key}.min`, parseInt(e.target.value) || 0)} />
                </Field>
                <Field label="Máxima" htmlFor={`${key}-max`}>
                  <TextInput id={`${key}-max`} type="number" value={jsonConfig.restricciones[key].max} onChange={(e) => handleJsonConfigChange(`restricciones.${key}.max`, parseInt(e.target.value) || 0)} />
                </Field>
              </Row>
            </div>
          ))}
        </div>
      </Section>

      {/* Campos opcionales */}
      <Section title="Campos opcionales del formulario" desc="Define los campos que este plan necesita y activa los que apliquen. El usuario deberá completarlos al contratar en yooldigital.">
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {catalogo.length === 0 && (
            <p style={{ fontSize: 13, color: FK.muted, fontStyle: "italic", margin: 0 }}>No hay campos definidos aún.</p>
          )}

          {catalogo.map((def) => {
            const activo = isActivo(def.campo);
            const campoActivo = getCampoActivo(def.campo);
            const opciones = campoActivo?.opciones ?? def.opciones ?? [];
            return (
              <div key={def.campo} style={{ border: "1px solid " + FK.border, borderRadius: 12, padding: 16, background: activo ? FK.surface : FK.field, boxShadow: activo ? "0 1px 2px rgba(16,38,64,.06)" : "none" }}>
                {/* Header: checkbox + label + eliminar */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 12 }}>
                  {/* Activar el campo es un ajuste on/off, no una casilla de una
                      lista: va con el switch del DS. `Módulos requeridos`, más
                      abajo, sigue con casillas porque ahí sí se eligen varios. */}
                  <div style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0, flexWrap: "wrap" }}>
                    <FkToggle on={activo} onChange={(v) => toggleCampo(def, v)}
                      aria-label={`${activo ? "Desactivar" : "Activar"} ${def.label}`} />
                    <span style={{ fontSize: 14, fontWeight: 600, color: FK.text }}>{def.label}</span>
                    <span style={{ fontSize: 12, color: FK.muted }}>({def.campo})</span>
                    {def.tipo && <Pill tone="muted">{TIPOS_CAMPO.find(t => t.value === def.tipo)?.label ?? def.tipo}</Pill>}
                  </div>
                  <button type="button" onClick={() => eliminarCampo(def.campo)} title="Eliminar campo" style={iconBtn}><X size={16} /></button>
                </div>

                <div style={{ paddingLeft: 25, display: "flex", flexDirection: "column", gap: 14 }}>
                  {/* Tipo de campo */}
                  <div style={{ maxWidth: 220 }}>
                    <div style={{ ...T.label, marginBottom: 6 }}>Tipo de campo</div>
                    <Select
                      value={def.tipo || "personal"}
                      options={TIPOS_CAMPO}
                      onChange={(e) => {
                        const newTipo = e.target.value;
                        handleJsonConfigChange("catalogo_campos", catalogo.map(c => c.campo === def.campo ? { ...c, tipo: newTipo } : c));
                        if (activo) {
                          handleJsonConfigChange("campos_opcionales", seleccionados.map(c => c.campo === def.campo ? { ...c, tipo: newTipo } : c));
                        }
                      }}
                    />
                  </div>

                  {/* Opciones */}
                  <div>
                    <div style={{ ...T.label, marginBottom: 8 }}>Opciones</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
                      {opciones.map((op) => (
                        <span key={op} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 8px", background: FK.surface, border: "1px solid " + FK.border, borderRadius: 8, fontSize: 13, color: FK.text }}>
                          {op}
                          <button type="button" onClick={() => eliminarOpcion(def.campo, op)} style={iconBtn}><X size={12} /></button>
                        </span>
                      ))}
                      {opciones.length === 0 && <span style={{ fontSize: 12.5, color: FK.muted }}>Sin opciones.</span>}
                    </div>
                    <div style={{ display: "flex", gap: 8, maxWidth: 340 }}>
                      <TextInput
                        value={nuevaOpcion[def.campo] || ""}
                        onChange={(e) => setNuevaOpcion(prev => ({ ...prev, [def.campo]: e.target.value }))}
                        placeholder="Agregar opción…"
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); agregarOpcion(def.campo); } }}
                      />
                      <FkOutline onClick={() => agregarOpcion(def.campo)}><Plus size={14} /></FkOutline>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Agregar nuevo campo */}
          <div style={{ border: "1.5px dashed " + FK.border, borderRadius: 12, padding: 16, background: FK.surface }}>
            <div style={{ ...T.label, marginBottom: 10 }}>Agregar nuevo campo</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-start" }}>
              <div style={{ flex: "1 1 160px", minWidth: 150 }}>
                <TextInput value={nuevoCampo.nombre} onChange={(e) => setNuevoCampo(prev => ({ ...prev, nombre: e.target.value }))} placeholder="ID (ej: rut_empresa)" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); agregarCampo(); } }} />
              </div>
              <div style={{ flex: "1 1 180px", minWidth: 160 }}>
                <TextInput value={nuevoCampo.label} onChange={(e) => setNuevoCampo(prev => ({ ...prev, label: e.target.value }))} placeholder="Etiqueta (ej: RUT Empresa)" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); agregarCampo(); } }} />
              </div>
              <div style={{ flex: "0 1 150px", minWidth: 140 }}>
                <Select value={nuevoCampo.tipo} options={TIPOS_CAMPO} onChange={(e) => setNuevoCampo(prev => ({ ...prev, tipo: e.target.value }))} />
              </div>
              <FkOutline onClick={agregarCampo}><Plus size={16} /> Agregar</FkOutline>
            </div>
          </div>
        </div>
      </Section>

      {/* Módulos requeridos */}
      <Section title="Módulos requeridos" desc="Módulos del flujo (flow) que este plan necesita activar.">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 6 }}>
          {modulosARenderizar.map((modulo) => {
            const checked = Array.isArray(jsonConfig.modulos_requeridos) && jsonConfig.modulos_requeridos.includes(modulo.id);
            return (
              <label key={modulo.id} htmlFor={`modulo-${modulo.id}`} style={{ display: "flex", alignItems: "center", gap: 9, padding: "7px 9px", borderRadius: 8, cursor: "pointer", border: "1px solid " + (checked ? FK.border : "transparent"), background: checked ? FK.surface : "transparent" }}>
                <input
                  type="checkbox"
                  id={`modulo-${modulo.id}`}
                  checked={checked}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    const currentModulos = Array.isArray(jsonConfig.modulos_requeridos) ? jsonConfig.modulos_requeridos : [];
                    const newModulos = isChecked ? [...currentModulos, modulo.id] : currentModulos.filter(m => m !== modulo.id);
                    handleJsonConfigChange('modulos_requeridos', newModulos);
                  }}
                  style={{ width: 15, height: 15, accentColor: FK.navy, cursor: "pointer", flexShrink: 0 }}
                />
                <span style={{ fontSize: 13, color: FK.text }}>
                  {modulo.label}
                  {modulo.adicional && <span style={{ marginLeft: 4, fontSize: 11, color: FK.warn }} title="Módulo presente en la configuración pero no en el catálogo de flow">(adicional)</span>}
                </span>
              </label>
            );
          })}
          {modulosARenderizar.length === 0 && (
            <p style={{ gridColumn: "1 / -1", fontSize: 13, color: FK.muted, margin: 0 }}>Cargando módulos disponibles…</p>
          )}
        </div>
      </Section>

      {/* Vista previa JSON (generado) */}
      <Section title="Vista previa JSON" desc="Se genera automáticamente a partir de la configuración de arriba.">
        <textarea
          value={formData.descripcion_json}
          readOnly
          rows={6}
          spellCheck={false}
          placeholder="El JSON se generará automáticamente…"
          style={{ width: "100%", boxSizing: "border-box", border: "1.5px solid " + FK.border, borderRadius: 8, background: FK.field, color: FK.sub, padding: "12px 14px", fontFamily: "var(--font-mono, 'SF Mono', ui-monospace, monospace)", fontSize: 12.5, lineHeight: 1.55, resize: "vertical" }}
        />
      </Section>

      {/* JSON array (editable) */}
      <Section title="JSON array" desc="Pega un JSON válido para actualizar el campo jsonarray del registro.">
        <JsonField
          name="jsonarray"
          value={formData.jsonarray}
          onChange={(e) => handleJsonConfigChange('jsonarray', e.target.value)}
          rows={8}
        />
      </Section>
    </div>
  );
}
